import {
  Card,
  CardBody,
  Input,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  Button,
} from "@windmill/react-ui";
// import ExportData from "components/common/ExportData";
import ExportDataXlsx from "components/common/ExportDataXlsx";
import NotFound from "components/table/NotFound";
import PageTitle from "components/Typography/PageTitle";
import { useState, useEffect, useContext } from "react";
import MainDrawer from "components/drawer/MainDrawer";
import DeleteModal from "components/modal/DeleteModal";
import useToggleDrawer from "hooks/useToggleDrawer";
import Somethingwentwrong from "pages/Somethingwentwrong";
import { SidebarContext } from "context/SidebarContext";
import { FiRefreshCcw } from "react-icons/fi";
import { Tabs } from "@mui/material";
import sidebar from "routes/sidebar";
import DataWrapper from "./DataWrapper";
import InputAreaDate from "components/form/InputAreaDate";
import LabelArea from "components/form/LabelArea";
import { toast } from "react-toastify";
import RevenueReportServices from "services/RevenueReport";
import SelectAgent from "components/form/SelectAgent";
import RevenueReportTable from "components/revenueReport/RevenueReportTable";

// Hypothetical useFilter hook
const useFilter = (data, itemsPerPage) => {
  const [dataTable, setDataTable] = useState(data?.data || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  // Synchronize dataTable with data
  useEffect(() => {
    // console.log("useFilter: safeData updated", data);
    setDataTable(data?.data || []);
    setCurrentPage(1); // Reset to first page on data change
  }, [data]);

  // Handle search filtering
  const handleSubmitRevenueReport = (e) => {
    const value = e.target.value || "";
    setSearchText(value);
    if (value) {
      const filtered = (data?.data || []).filter((item) =>
        Object.values(item).some(
          (val) =>
            val && val.toString().toLowerCase().includes(value.toLowerCase()),
        ),
      );
      setDataTable(filtered);
    } else {
      setDataTable(data?.data || []);
    }
    setCurrentPage(1);
  };

  // Reset function for external use
  const resetDataTable = () => {
    setDataTable([]);
    setSearchText("");
    setCurrentPage(1);
  };

  return {
    dataTable,
    serviceData: data?.data || [],
    currentPage,
    setCurrentPage,
    handleSubmitRevenueReport,
    RevenueReportRef: { current: null }, // Mock ref for search input
    resetDataTable,
  };
};

const RevenueReport = () => {
  const { toggleDrawer, isUpdate, setIsUpdate } = useContext(SidebarContext);
  const { title, serviceId } = useToggleDrawer();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchStartDate, setSearchStartDate] = useState(null);
  const [searchEndDate, setSearchEndDate] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRangeType, setDateRangeType] = useState("0");
  const [agentID, setAgentId] = useState("");
  const [RevenueReportData, setRevenueReportData] = useState({ data: [] });
  const [status, setStatus] = useState("");

  const adminInfo = localStorage.getItem("adminInfo")
    ? JSON.parse(localStorage.getItem("adminInfo"))
    : null;
  const isAdmin = adminInfo?.id === 1;

  const hideExport =
    isAdmin ||
    adminInfo?.permission?.some(
      (perm) => perm.id_tab === "RevenueReport" && perm.export === 1,
    );

  const hideView =
    isAdmin ||
    adminInfo?.permission?.some(
      (perm) => perm.id_tab === "RevenueReport" && perm.view === 1,
    );

  const hideEdit =
    isAdmin ||
    adminInfo?.permission?.some(
      (perm) => perm.id_tab === "RevenueReport" && perm.edit === 1,
    );

  const hideDelete =
    isAdmin ||
    adminInfo?.permission?.some(
      (perm) => perm.id_tab === "RevenueReport" && perm.delete === 1,
    );

  const safeData = RevenueReportData;

  const {
    RevenueReportRef,
    serviceData,
    dataTable,
    handleSubmitRevenueReport,
    currentPage,
    resetDataTable,
  } = useFilter(safeData, itemsPerPage);
  useEffect(() => {
    if (dataTable.length !== 0) {
      setItemsPerPage(dataTable.length);
    }
  }, [dataTable]);

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const fetchRevenueReport = async () => {
    setLoading(true);
    setError(null);
    setRevenueReportData({ data: [] }); // Clear data immediately
    setStatus(false);
    resetDataTable(); // Reset dataTable in useFilter

    try {
      const payload = {
        agent_id: agentID || null,
        start_date: formatDate(searchStartDate),
        end_date: formatDate(searchEndDate),
        dateRangeType: dateRangeType || null,
      };

      const response = await RevenueReportServices.getRevenue(payload);

      if (response.status) {
        const resultData = response.data;
        setRevenueReportData({ data: resultData });
        setStatus(true);

        if (resultData.length === 0) {
          // toast.info("No Revenue Report data found"); // Use info instead of error
        }
      } else {
        setRevenueReportData({ data: [] });
        setStatus(false);
        // toast.info("No Revenue Report data found"); // Use info instead of error
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch Revenue Report";
      console.error("Error fetching Revenue Report data:", errorMessage);
      setError(errorMessage);
      setStatus(false);
      setRevenueReportData({ data: [] });
      toast.error(`Error fetching Revenue Report data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueReport();
    setIsUpdate(false);
  }, [isUpdate]);

  const handleSearch = () => {
    setIsUpdate(true);
  };

  const handleRefresh = () => {
    setSearchStartDate(null);
    setSearchEndDate(null);
    setSelectedAgentId("");
    setAgentId("");
    setRevenueReportData({ data: [] });
    setStatus("");
    setError(null);
    resetDataTable(); // Reset dataTable in useFilter
    setIsUpdate(true);
  };
  // const transformRevenueReportForExport = (data = []) => {
  //   let totalSales = 0;
  //   let totalBill = 0;
  //   let totalTransportExclGST = 0;

  //   const rows = data.map((item, index) => {
  //     const salesAmount = Number(item.sales_amount) || 0;
  //     const billAmount = Number(item.total_vehicle_bill_amount) || 0;
  //     const transportPriceExclGST = Number(item.transport_price_excl_gst) || 0;

  //     const profitLossValue = salesAmount - billAmount;
  //     const formatDate = (d) =>
  //       d ? new Date(d).toLocaleDateString("en-GB") : "-";
  //     // % based on transport excl GST
  //     const profitPercentage =
  //       transportPriceExclGST > 0
  //         ? ((profitLossValue / transportPriceExclGST) * 100).toFixed(1)
  //         : "0.0";

  //     // accumulate totals
  //     totalSales += salesAmount;
  //     totalBill += billAmount;
  //     totalTransportExclGST += transportPriceExclGST;

  //     return {
  //       S_No: index + 1,
  //       Reference_No: item.ref_no || "-",
  //       Agent_Company: item.agent_company_name || "-",
  //       Client_Name: item.client_name || "-",
  //       Arrival_City: item.arrival_city || "-",
  //       Departure_City: item.departure_city || "-",
  //       Start_Date: formatDate(item.start_date),
  //       End_Date: formatDate(item.end_date),
  //       Transport_Price_Excl_GST: transportPriceExclGST || "-",
  //       Sales_Amount: salesAmount || "-",
  //       Vehicle_Bill_Amount: billAmount || "-",
  //       Profit_Loss_Value: profitLossValue,
  //       Profit_Percentage: `${profitPercentage}%`,
  //       Status: profitLossValue >= 0 ? "Profit" : "Loss",
  //     };
  //   });

  //   // Calculate totals
  //   const totalProfitLoss = totalSales - totalBill;
  //   const totalProfitPercentage =
  //     totalTransportExclGST > 0
  //       ? ((totalProfitLoss / totalTransportExclGST) * 100).toFixed(1)
  //       : "0.0";

  //   // Push totals row
  //   rows.push({
  //     S_No: "",
  //     Reference_No: "TOTAL",
  //     Agent_Company: "",
  //     Client_Name: "",
  //     Arrival_City: "",
  //     Departure_City: "",
  //     Start_Date: "",
  //     End_Date: "",
  //     Transport_Price_Excl_GST: totalTransportExclGST,
  //     Sales_Amount: totalSales,
  //     Vehicle_Bill_Amount: totalBill,
  //     Profit_Loss_Value: totalProfitLoss,
  //     Profit_Percentage: `${totalProfitPercentage}%`,
  //     Status: totalProfitLoss >= 0 ? "Profit" : "Loss",
  //   });

  //   return rows;
  // };

  // Debugging logs
  // useEffect(() => {
  //   console.log("RevenueReportData:", RevenueReportData);
  //   console.log("dataTable:", dataTable);
  //   console.log("error:", error);
  // }, [RevenueReportData, dataTable, error]);

  const transformRevenueReportForExport = (data = []) => {
    let totalProfit = 0;
    let totalBill = 0;
    let totalTransportExclGST = 0;

    const formatDate = (date) =>
      date ? new Date(date).toLocaleDateString("en-GB") : "-";

    const rows = data.map((item, index) => {
      const salesAmount = Number(item?.sales_amount) || 0;
      const billAmount = Number(item?.total_vehicle_bill_amount) || 0;
      const transportPriceExclGST = Number(item?.transport_price_excl_gst) || 0;

      // Profit/Loss = Transport price excluding GST - Vehicle bill
      const profitLossValue = transportPriceExclGST - billAmount;

      const profitPercentage =
        transportPriceExclGST > 0
          ? ((profitLossValue / transportPriceExclGST) * 100).toFixed(1)
          : "0.0";

      // Accumulate totals
      totalProfit += profitLossValue;
      totalBill += billAmount;
      totalTransportExclGST += transportPriceExclGST;

      return {
        S_No: index + 1,
        Reference_No: item?.ref_no || "-",
        Agent_Company: item?.agent_company_name || "-",
        Client_Name: item?.client_name || "-",
        Arrival_City: item?.arrival_city || "-",
        Departure_City: item?.departure_city || "-",
        Start_Date: formatDate(item?.start_date),
        End_Date: formatDate(item?.end_date),
        Transport_Price_Excl_GST: transportPriceExclGST || "-",
        Sales_Amount: salesAmount || 0,
        Vehicle_Bill_Amount: billAmount || "-",
        Profit_Loss_Value: profitLossValue,
        Profit_Percentages: `${profitPercentage}%`,
        Status: profitLossValue >= 0 ? "Profit" : "Loss",
      };
    });

    // Total profit percentage
    const totalProfitPercentage =
      totalTransportExclGST > 0
        ? ((totalProfit / totalTransportExclGST) * 100).toFixed(1)
        : "0.0";

    // TOTAL row
    rows.push({
      S_No: "",
      Reference_No: "TOTAL",
      Agent_Company: "",
      Client_Name: "",
      Arrival_City: "",
      Departure_City: "",
      Start_Date: "",
      End_Date: "",
      Transport_Price_Excl_GST: totalTransportExclGST,
      Sales_Amount: data.reduce(
        (total, item) => total + (Number(item?.sales_amount) || 0),
        0,
      ),
      Vehicle_Bill_Amount: totalBill,
      Profit_Loss_Value: totalProfit,
      Profit_Percentages: `${totalProfitPercentage}%`,
      Status: totalProfit >= 0 ? "Profit" : "Loss",
    });

    return rows;
  };
  return (
    <>
      <DeleteModal id={serviceId} title={title} />
      <MainDrawer />
      <PageTitle sidebar={sidebar().sidebar} />
      <Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800">
        <CardBody>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4">
                {/* Agent Select */}
                <div className="flex items-center gap-2">
                  <SelectAgent
                    name="agent_id"
                    label="Agent Name"
                    value={agentID}
                    onChange={(e) => setAgentId(e.target.value)}
                  />
                </div>

                {/* Date Range Select */}
                <div className="flex items-center gap-2">
                  <LabelArea label="Date Range" />
                  <select
                    className="border rounded px-2 py-1 h-10"
                    value={dateRangeType}
                    onChange={(e) => setDateRangeType(e.target.value)}
                  >
                    <option value="0">This Week</option>
                    <option value="1">This Month</option>
                    <option value="2">Last 3 Months</option>
                    <option value="3">Last 6 Months</option>
                    <option value="4">Last Year</option>
                    <option value="5">Custom</option>
                  </select>
                </div>
              </div>

              {/* Custom Date Pickers */}
              {dateRangeType === "5" && (
                <>
                  <div className="flex items-center gap-2 mt-2">
                    <LabelArea label="Start Date" />
                    <div className="w-50">
                      <InputAreaDate
                        value={searchStartDate}
                        onChange={(date) => setSearchStartDate(date)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <LabelArea label="End Date" />
                    <div className="w-50">
                      <InputAreaDate
                        min={searchStartDate}
                        value={searchEndDate}
                        onChange={(date) => setSearchEndDate(date)}
                      />
                    </div>
                  </div>
                </>
              )}

              <Button className="rounded-md h-10 px-8" onClick={handleSearch}>
                Search
              </Button>
              <FiRefreshCcw onClick={handleRefresh} cursor="pointer" />
            </div>
          </div>
          <div className="flex justify-between items-end flex-wrap gap-3 w-full pt-4 px-2">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Input
                  ref={RevenueReportRef}
                  className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white"
                  type="search"
                  name="search"
                  autoComplete="off"
                  placeholder="Quick Search"
                  onChange={handleSubmitRevenueReport}
                />
                <Button onClick={handleSubmitRevenueReport}>Search</Button>
              </div>
            </div>
            {hideExport && (
              <div className="pr-2">
                <ExportDataXlsx
                  title="RevenueReport"
                  exportDataXlsx={transformRevenueReportForExport(dataTable)}
                  fileNames="RevenueReport"
                />
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <DataWrapper loading={loading} error={error} status={status}>
        {/* {console.log("dataTable:", dataTable)} */}
        {dataTable?.length > 0 ? (
          <TableContainer
            className="mb-4"
            style={{
              maxHeight: "65vh",
              overflow: "scroll",
              scrollbarWidth: "thin",
            }}
          >
            <Table>
              <TableHeader className="custom-table-header capitalize sticky top-0 z-10">
                <tr>
                  <TableCell className="text-center">Sl.No.</TableCell>
                  <TableCell className="text-center">
                    Agent ID / Booking ID
                  </TableCell>
                  <TableCell className="text-center">Agent Name</TableCell>

                  <TableCell className="text-center">
                    Arrival / Departure
                  </TableCell>
                  <TableCell className="text-center">Travel Date</TableCell>
                  <TableCell className="text-center">T.Sales</TableCell>
                  <TableCell className="text-center">T.Selling</TableCell>
                  <TableCell className="text-center">gst</TableCell>

                  <TableCell className="text-center">Bill Amount</TableCell>
                  <TableCell className="text-center">Profit</TableCell>
                  <TableCell className="text-center">Status</TableCell>
                </tr>
              </TableHeader>
              <RevenueReportTable
                RevenueReport={dataTable}
                perpage={itemsPerPage}
                currentPage={currentPage}
                hideDelete={hideDelete}
                hideEdit={hideEdit}
                hideExport={hideExport}
                hideView={hideView}
              />
            </Table>
          </TableContainer>
        ) : dataTable?.length === 0 ? (
          <NotFound title="No Revenue Report data found." />
        ) : error ? (
          <Somethingwentwrong
            title={error || "Something went wrong. Please try again later."}
          />
        ) : null}
      </DataWrapper>
    </>
  );
};

export default RevenueReport;
